/**
 * 新しい申請 `/hr/requests/new?kind=`。入社・退社・異動・変更・休職・復職の 5 入口を 1 つに畳んだ
 * ウィザード(種別 → 対象者 → 内容 → 確認)。右に申請の概要と承認ルート、下に 戻る / 次へ / 申請する。
 *
 * 中身(入力欄・検証・API)は旧 Modal の RequestForm をそのまま使う。旧パス
 * (/hr/onboarding など)は RequestRedirects で `?kind=` 付きのここへ飛ばす。
 *
 * 入社申請だけは対象者段で登録済みの人を選ばず、入社する人を新規入力する(RequestNewEmployee)。
 * 「申請する」で従業員の登録と入社申請を 1 回で作る(POST /api/hr/onboarding。片方だけ残らない)。
 * 登録してから申請画面で同じ人を探し直す手間を無くすため、入口を申請の側に寄せた。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Space, message } from "antd";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useApi } from "../../lib/useApi";
import { FormPage, KeyValueList, Section, Steps, type FormStep, type KvItem } from "../../ui";
import { WF_KINDS, kindLabel } from "./constants";
import { errorText } from "./hooks";
import { useNewEmployee } from "./newEmployee";
import { RequestConfirm, RequestContent, RequestNewEmployee, RequestTarget } from "./RequestForm";
import { KIND_DESC, includesGws, targetItems, useRequestDraft } from "./requestDraft";
import { normalizeKind } from "./requestKinds";
import { useInvalidateWf, useWfPerms } from "./wfHooks";
import { draftRoute } from "./wfRoute";

const STEPS: FormStep[] = [
  { key: "kind", label: "種別" },
  { key: "target", label: "対象者" },
  { key: "content", label: "内容" },
  { key: "confirm", label: "確認" },
];
const ORDER = STEPS.map((s) => s.key);

export function NewRequestPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const kind = normalizeKind(sp.get("kind"));
  const { me, canOperate } = useWfPerms();
  // `?kind=` 付きで来たら種別の段は飛ばす(旧パスからの redirect・一覧の「新しい申請」)
  const [step, setStep] = useState<string>(kind ? "target" : "kind");
  // 入社申請の対象者(新規入力)。他の種別では使わない(登録済みの人を選ぶ)
  const newEmp = useNewEmployee();
  const isOnboard = kind === "onboard";
  const draft = useRequestDraft(kind ?? "", (id) => navigate(`/hr/wf-requests/${id}`), {
    employee: isOnboard ? newEmp.draftEmployee : undefined,
  });
  const api = useApi();
  const invalidate = useInvalidateWf();
  const onboard = useMutation({
    mutationFn: () =>
      api.createOnboarding({ employee: newEmp.input(draft.startDate), request: draft.requestBody() }),
    onSuccess: (r) => {
      void message.success(`従業員 ${r.employee.name} を登録し、入社申請 #${r.request.id} を出した`);
      invalidate();
      navigate(`/hr/wf-requests/${r.request.id}`);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const idx = ORDER.indexOf(step);
  /** GWS のアカウント発行を選んだのにメールが空か。対象者段では項目が未定なので、内容段と確認で見る */
  const gwsEmailMissing = () => {
    if (!isOnboard || newEmp.value.email.trim()) return false;
    if (!includesGws(draft.tpl?.data?.items ?? [], draft.itemIds)) return false;
    void message.error("GWS のアカウント発行を選んだのでメールアドレスは必須");
    setStep("target");
    return true;
  };
  const pickKind = (k: string) => {
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("kind", k);
        return next;
      },
      { replace: true },
    );
    setStep("target");
  };
  const next = () => {
    if (step === "kind") {
      if (!kind) {
        void message.error("種別を選ぶ");
        return;
      }
    } else if (step === "target") {
      const missing = isOnboard ? newEmp.missing() : draft.empId ? undefined : "対象者を選ぶ";
      if (missing) {
        void message.error(missing);
        return;
      }
    } else if (step === "content" && isOnboard && !draft.startDate) {
      // 入社日は従業員の入社日も兼ねるので、入社申請だけは必須(サーバーも 400 で返す)
      void message.error("入社日は必須");
      return;
    } else if (step === "content" && gwsEmailMissing()) {
      return;
    }
    setStep(ORDER[Math.min(idx + 1, ORDER.length - 1)]);
  };
  const back = () => setStep(ORDER[Math.max(idx - 1, 0)]);
  const submit = () => {
    if (!isOnboard) {
      draft.submit();
      return;
    }
    // 段を飛ばして確認に来られる(上の手順を押す)ので、ここでも同じ検査をする
    const missing = newEmp.missing() ?? (draft.startDate ? undefined : "入社日は必須");
    if (missing) {
      void message.error(missing);
      return;
    }
    if (gwsEmailMissing()) return;
    if (!draft.approverReady()) return;
    onboard.mutate();
  };
  const close = () => navigate(kind ? `/hr/requests/list?kind=${kind}` : "/hr/requests/list");

  const summary: KvItem[] = [
    { label: "種別", value: kind ? `${kindLabel(kind)}申請` : undefined, always: true },
    ...targetItems(draft),
    { label: "テンプレート", value: draft.tpl.data?.name, always: true },
    { label: draft.dateLabel, value: draft.startDate, always: true },
    { label: "SaaS", value: draft.itemIds.length ? `${draft.itemIds.length} 件` : undefined },
    { label: "工程", value: draft.stepIds.length ? `${draft.stepIds.length} 件` : undefined },
  ];
  const aside = (
    <>
      <Section title="申請の概要">
        <KeyValueList items={summary} narrow unsetLabel="未選択" />
      </Section>
      <Section title="承認ルート">
        <Steps items={draftRoute(draft.defaults.data?.required_approvals ?? 1, me?.username ?? "")} />
      </Section>
    </>
  );

  const footer = canOperate ? (
    <>
      <Button onClick={back} disabled={idx <= 0}>
        戻る
      </Button>
      <span className="ui-filter-spacer" />
      {step === "confirm" ? (
        <Button type="primary" loading={draft.create.isPending || onboard.isPending} onClick={submit}>
          申請する
        </Button>
      ) : (
        <Button type="primary" onClick={next} disabled={step === "kind" && !kind}>
          次へ
        </Button>
      )}
    </>
  ) : (
    <Button onClick={close}>申請一覧へ</Button>
  );

  return (
    <FormPage
      title="新しい申請"
      description={kind ? KIND_DESC[kind] : "入社・退社・異動・変更・休職・復職の申請を 1 つの入口から出す。"}
      steps={STEPS}
      current={step}
      onStep={setStep}
      onClose={close}
      aside={aside}
      footer={footer}
    >
      {!canOperate ? (
        <Alert
          type="info"
          showIcon
          message="申請を出せるのは hr:operator だけ"
          description="このロールでは申請の一覧・詳細を読めるが、新しく出すことはできない。"
        />
      ) : step === "kind" ? (
        <Section title="種別を選ぶ">
          <div className="ux-kind-grid" role="radiogroup" aria-label="申請の種別">
            {WF_KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                role="radio"
                aria-checked={kind === k.value}
                className={`ux-kind-card${kind === k.value ? " is-selected" : ""}`}
                onClick={() => pickKind(k.value)}
              >
                <strong>{k.label}申請</strong>
                <span>{KIND_DESC[k.value]}</span>
              </button>
            ))}
          </div>
        </Section>
      ) : step === "target" ? (
        <Section title={`対象者(${kindLabel(kind ?? "")}申請)`}>
          {isOnboard ? <RequestNewEmployee emp={newEmp} /> : <RequestTarget draft={draft} />}
        </Section>
      ) : step === "content" ? (
        <Section title="内容">
          <RequestContent draft={draft} />
        </Section>
      ) : (
        <Section title="この内容で申請する">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert type="info" showIcon message="直すときは上の手順を押して戻る。「申請する」で承認ルートに乗る。" />
            <RequestConfirm draft={draft} />
          </Space>
        </Section>
      )}
    </FormPage>
  );
}
